import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { superAdminAPI } from '../../services/api';
import './SuperAdminDashboard.scss';

const SuperAdminDashboard = () => {
  const { superAdmin } = useSuperAdmin();

  // Test email state
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testEmailError, setTestEmailError] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedDaysLeft, setSelectedDaysLeft] = useState(5);

  const { data: overviewData, isLoading } = useQuery({
    queryKey: ['superadmin-overview'],
    queryFn: superAdminAPI.getOverview
  });

  // Fetch organizations for test email dropdown
  const { data: orgsData } = useQuery({
    queryKey: ['superadmin-orgs-for-test'],
    queryFn: () => superAdminAPI.getOrganizations({ limit: 200 }),
    select: (data) => {
      const orgs = data?.data?.data || data?.data || [];
      return Array.isArray(orgs) ? orgs : [];
    },
  });

  const organizations = orgsData || [];

  const overview = overviewData?.data?.data || {};

  const stats = [
    {
      label: 'Total Organizations',
      value: overview?.organizations?.total || 0,
      change: overview?.organizations?.active || 0,
      changeLabel: 'active',
      icon: '🏢',
      color: 'primary'
    },
    {
      label: 'Trial Organizations',
      value: overview?.organizations?.trial || 0,
      change: overview?.organizations?.suspended || 0,
      changeLabel: 'suspended',
      icon: '⏳',
      color: 'warning'
    },
    {
      label: 'Total Users',
      value: overview?.users?.total || 0,
      change: overview?.users?.active || 0,
      changeLabel: 'active',
      icon: '👥',
      color: 'success'
    },
    {
      label: 'Plans Active',
      value: overview?.organizations?.byPlan?.length || 3,
      change: 0,
      changeLabel: '',
      icon: '📋',
      color: 'info'
    }
  ];

  const handleSendTestEmail = async () => {
    if (!selectedOrgId) {
      setTestEmailError('Please select an organization');
      return;
    }
    setTestEmailLoading(true);
    setTestEmailError(null);
    setTestEmailResult(null);
    try {
      const response = await superAdminAPI.sendTestSubscriptionEmail(selectedOrgId, selectedDaysLeft);
      setTestEmailResult(response.data?.data || response.data || {});
    } catch (error) {
      setTestEmailError(error.response?.data?.message || error.message || 'Failed to send test email');
    } finally {
      setTestEmailLoading(false);
    }
  };

  const daysLeftOptions = [
    { value: 5, label: '5 days left' },
    { value: 4, label: '4 days left' },
    { value: 3, label: '3 days left' },
    { value: 2, label: '2 days left' },
    { value: 1, label: '1 day left (tomorrow)' },
    { value: 0, label: '0 days left (today)' },
    { value: -1, label: 'Expired' },
  ];

  if (isLoading) {
    return (
      <div className="superadmin-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="superadmin-dashboard">
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Platform Overview</h1>
          <p>Welcome back, {superAdmin?.name || 'Super Admin'}</p>
        </div>
        <div className="header-right">
          <Link to="/superadmin/organizations" className="btn btn-primary">
            View Organizations
          </Link>
          <Link to="/superadmin/plans" className="btn btn-outline">
            Manage Plans
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.color}`}>
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <div className="stat-value">{stat.value.toLocaleString()}</div>
              <div className="stat-label">{stat.label}</div>
              {stat.change > 0 && (
                <div className="stat-change">
                  {stat.change.toLocaleString()} {stat.changeLabel}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Organizations by Plan */}
      {overview?.organizations?.byPlan && overview.organizations.byPlan.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2>Organizations by Plan</h2>
          </div>
          <div className="card-body">
            <div className="plan-distribution">
              {overview.organizations.byPlan.map((item, index) => (
                <div key={index} className="plan-item">
                  <div className="plan-name">{item.planName || 'Unknown'}</div>
                  <div className="plan-count">{item.count}</div>
                  <div className="plan-bar">
                    <div
                      className="plan-bar-fill"
                      style={{
                        width: `${(item.count / overview.organizations.total) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="actions-grid">
          <Link to="/superadmin/organizations" className="action-card">
            <div className="action-icon">🏢</div>
            <div className="action-content">
              <h3>Manage Organizations</h3>
              <p>View, create, and manage all organizations</p>
            </div>
          </Link>
          <Link to="/superadmin/plans" className="action-card">
            <div className="action-icon">📋</div>
            <div className="action-content">
              <h3>Manage Plans</h3>
              <p>Create and edit subscription plans</p>
            </div>
          </Link>
          <Link to="/superadmin/admins" className="action-card">
            <div className="action-icon">👥</div>
            <div className="action-content">
              <h3>Manage Admins</h3>
              <p>Add and manage platform administrators</p>
            </div>
          </Link>
          <Link to="/superadmin/analytics" className="action-card">
            <div className="action-icon">📊</div>
            <div className="action-content">
              <h3>View Analytics</h3>
              <p>Platform usage and revenue metrics</p>
            </div>
          </Link>
        </div>
      </div>

      {/* System Tools */}
      <div className="system-tools">
        <h2>System Tools</h2>
        <div className="tools-card">
          <div className="tool-section">
            <div className="tool-header">
              <div className="tool-icon">✉️</div>
              <div className="tool-info">
                <h3>Send Test Subscription Email</h3>
                <p>Send a subscription expiry test email to a specific organization. Subscription emails are sent automatically every morning at 9:00 AM. Use this to preview what the email looks like.</p>
              </div>
            </div>
            <div className="tool-controls">
              <select
                className="tool-select"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                disabled={testEmailLoading}
              >
                <option value="">Select organization...</option>
                {organizations.map((org) => (
                  <option key={org._id} value={org._id}>
                    {org.name} ({org.email})
                  </option>
                ))}
              </select>
              <select
                className="tool-select tool-select-days"
                value={selectedDaysLeft}
                onChange={(e) => setSelectedDaysLeft(Number(e.target.value))}
                disabled={testEmailLoading}
              >
                {daysLeftOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary tool-btn"
                onClick={handleSendTestEmail}
                disabled={testEmailLoading || !selectedOrgId}
              >
                {testEmailLoading ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send Test Email'
                )}
              </button>
            </div>
            {testEmailError && (
              <div className="notif-result error">
                <span className="result-icon">❌</span>
                <span>{testEmailError}</span>
              </div>
            )}
            {testEmailResult && !testEmailError && (
              <div className="notif-result success">
                <div className="result-row">
                  <span className="result-label">Organization</span>
                  <span className="result-value">{testEmailResult.organizationName}</span>
                </div>
                <div className="result-row">
                  <span className="result-label">Email Sent To</span>
                  <span className="result-value">{testEmailResult.organizationEmail}</span>
                </div>
                <div className="result-row">
                  <span className="result-label">Plan</span>
                  <span className="result-value">{testEmailResult.planName}</span>
                </div>
                <div className="result-row">
                  <span className="result-label">Days Left Simulated</span>
                  <span className="result-value highlight-green">
                    {testEmailResult.daysLeft === -1 ? 'Expired' : `${testEmailResult.daysLeft} days`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;